import React, { useState, useRef, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { Download, Printer, Sparkles, X, QrCode, ArrowLeft, Eye, ExternalLink, Image, Palette, Type, Save, Upload } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const LS_KEY = 'kit_pub_settings_v1';

const FONT_URL = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,900;1,400&family=Raleway:wght@300;400;500;600;700&family=Cormorant+Garamond:wght@300;400;600;700&family=Montserrat:wght@300;400;500;600;700&family=Lato:wght@300;400;700&display=swap';
function ensureFont() {
  if (!document.querySelector('[data-kit-font]')) {
    const l = document.createElement('link');
    l.rel = 'stylesheet'; l.href = FONT_URL; l.setAttribute('data-kit-font','1');
    document.head.appendChild(l);
  }
}

const FONT_OPTIONS = [
  { label: 'Playfair Display — Élégant', value: "'Playfair Display',serif" },
  { label: 'Raleway — Moderne',          value: "'Raleway',sans-serif" },
  { label: 'Cormorant Garamond — Luxe',  value: "'Cormorant Garamond',serif" },
  { label: 'Montserrat — Pro',           value: "'Montserrat',sans-serif" },
  { label: 'Lato — Neutre',             value: "'Lato',sans-serif" },
  { label: 'Georgia — Classique',       value: "Georgia,serif" },
];

const FORMAT_SHORT = {
  a4_portrait: 'A4 ↕',
  a4_paysage:  'A4 ↔',
  a5_portrait: 'A5',
  carre:       'Carré',
  a3_portrait: 'A3',
  carte_visite:'Carte',
};

const FIELD_LABELS = {
  titre:      'Titre principal',
  sous_titre: 'Sous-titre',
  services:   'Services mis en avant',
  cta:        'Texte sous le QR code',
};

const FORMATS = [
  { id:'a4_portrait',  label:'A4 Portrait',    sub:'21 × 29,7 cm', icon:'📄', w:794,  h:1123 },
  { id:'a4_paysage',   label:'A4 Paysage',      sub:'29,7 × 21 cm', icon:'🖼️', w:1123, h:794  },
  { id:'a5_portrait',  label:'A5 Portrait',     sub:'14,8 × 21 cm', icon:'📋', w:559,  h:794  },
  { id:'carre',        label:'Carré Réseaux',   sub:'20 × 20 cm',   icon:'⬛', w:756,  h:756  },
  { id:'a3_portrait',  label:'A3 Grand Format', sub:'29,7 × 42 cm', icon:'🗞️', w:1123, h:1587 },
  { id:'carte_visite', label:'Carte de visite', sub:'8,5 × 5,5 cm', icon:'💳', w:321,  h:208  },
];

const EXEMPLE = {
  titre:    'Estimez votre bien au juste prix',
  sous_titre:'Audit immobilier offert — Confidentiel & sans engagement',
  services: [
    'Estimation basée sur les données notariales',
    'Analyse DVF & transactions du marché local',
    "Radar foncier & opportunités d'investissement",
    'Rapport complet remis sous 48 h — offert',
  ],
  cta: 'Scannez le QR code ou visitez notre site',
  stats: [
    { value:'100%', label:'Gratuit' },
    { value:'48h',  label:'Délai de réponse' },
    { value:'500+', label:'Clients accompagnés' },
  ],
};

const COLOR_PRESETS_BG = [
  { name:'Navy Luxe',      value:'#0B1426' },
  { name:'Anthracite',     value:'#1c1c1e' },
  { name:'Bordeaux',       value:'#3B0A1A' },
  { name:'Vert Forêt',     value:'#0D2B1E' },
  { name:'Bleu Nuit',      value:'#0a1628' },
  { name:'Brun Chaud',     value:'#2C1A0E' },
  { name:'Gris Ardoise',   value:'#2D3748' },
  { name:'Violet Profond', value:'#1A0B3B' },
  { name:'Vert Olive',     value:'#1A2B0A' },
  { name:'Rouge Rubis',    value:'#4A0000' },
  { name:'Bleu Acier',     value:'#1B3A5C' },
  { name:'Noir Charbon',   value:'#111111' },
];
const COLOR_PRESETS_ACCENT = [
  { name:'Or classique', value:'#C9A96E' },
  { name:'Or vif',       value:'#F0C040' },
  { name:'Champagne',    value:'#E8D5B0' },
  { name:'Cuivre',       value:'#C87941' },
  { name:'Argent',       value:'#C0C0C0' },
  { name:'Blanc pur',    value:'#F5F5F5' },
  { name:'Rose Gold',    value:'#E8B4B8' },
  { name:'Turquoise',    value:'#4ECDC4' },
  { name:'Terracotta',   value:'#E07A5F' },
  { name:'Lavande',      value:'#B8A0D0' },
  { name:'Or Rose',      value:'#D4A574' },
  { name:'Vert Sauge',   value:'#8FAF8A' },
];

function hexToRgba(hex, a) {
  if (!hex || hex.length < 7) return `rgba(0,0,0,${a})`;
  const r=parseInt(hex.slice(1,3),16), g=parseInt(hex.slice(3,5),16), b=parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
}

/* ════════════════════════════════════════════════════
   PORTRAIT (A4 · A5 · A3 · Carré)
════════════════════════════════════════════════════ */
function PortraitPoster({ branding, texts, qrUrl, w, h, colors, textStyle = {}, isSquare = false }) {
  const { logoUrl, agencyName, phone, website } = branding;
  const { titre, sous_titre, services, cta, stats } = texts;
  const { headerBg, accent, headerImg, headerOverlay } = colors;

  /* Tout est relatif à w=794 (A4 portrait de référence) */
  const u = w / 794;

  /* Zones de hauteur */
  const headerH = Math.round(h * (isSquare ? 0.22 : 0.32));
  const footerH = Math.round(h * (isSquare ? 0.06 : 0.08));
  const bodyH   = h - headerH - footerH;

  /* Typographie — beaucoup plus grande */
  const fAgency = Math.round(24 * u);
  const fSub2   = Math.round(10 * u);   // sous le nom agence
  const fTitle  = Math.round(36 * u);   // titre accroche
  const fSubT   = Math.round(15 * u);   // sous-titre
  const fSvc    = Math.round(16 * u);   // services
  const fCta    = Math.round(15 * u);   // texte QR
  const fContact= Math.round(13 * u);
  const fStat   = Math.round(22 * u);   // chiffres stats
  const fStatLbl= Math.round(10 * u);
  const fFooter = Math.round(10 * u);
  const logoH   = Math.round(58 * u);
  const scaledLogoH = Math.round(logoH * (textStyle?.agencySize||100)/100);
  const padH    = Math.round(44 * u);
  const iconSz  = Math.round((isSquare ? 22 : 30) * u);
  const qrSize  = Math.round(200 * u * (textStyle.qrScale||100)/100);

  const showStats = !!stats && stats.some(s => s.value);

  return (
    <div style={{ width:w, height:h, background:'#fff', display:'flex', flexDirection:'column', fontFamily:"'Raleway','Segoe UI',sans-serif", overflow:'hidden' }}>

      {/* ══ HEADER ══ */}
      <div style={{ height:headerH, flexShrink:0, position:'relative', overflow:'hidden', background: headerImg ? 'transparent' : headerBg, display:'flex', flexDirection:'column', justifyContent:'space-between', padding:`${Math.round((isSquare?14:28)*u)}px ${padH}px ${Math.round((isSquare?10:24)*u)}px` }}>
        {headerImg && <>
          <img src={headerImg} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', objectPosition:'center' }} />
          <div style={{ position:'absolute', inset:0, background:hexToRgba(headerBg, headerOverlay??0.75) }} />
        </>}
        {/* Cercle déco */}
        <div style={{ position:'absolute', right:-Math.round(60*u), top:-Math.round(60*u), width:Math.round(260*u), height:Math.round(260*u), borderRadius:'50%', border:`${Math.round(1.5*u)}px solid ${hexToRgba(accent,0.15)}`, zIndex:1 }} />

        {/* Logo + nom */}
        <div style={{ display:'flex', alignItems:'center', gap:Math.round(16*u), position:'relative', zIndex:2 }}>
          {logoUrl
            ? <img src={logoUrl} alt="logo" style={{ height:scaledLogoH, maxWidth:Math.round(160*u), objectFit:'contain' }} />
            : <div style={{ height:scaledLogoH, width:scaledLogoH, borderRadius:Math.round(10*u), background:hexToRgba(accent,0.18), border:`${Math.round(2*u)}px solid ${hexToRgba(accent,0.5)}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ color:accent, fontWeight:700, fontSize:Math.round(24*u) }}>{(agencyName||'A').charAt(0)}</span>
              </div>
          }
          <div>
            <div style={{ color:'#fff', fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:Math.round(fAgency*(textStyle?.agencySize||100)/100), letterSpacing:'0.03em' }}>{agencyName}</div>
            <div style={{ color:hexToRgba(accent,0.8), fontSize:fSub2, fontWeight:300, letterSpacing:'0.14em', textTransform:'uppercase', marginTop:Math.round(3*u) }}>Immobilier de prestige</div>
          </div>
        </div>

        {/* Titre */}
        <div style={{ position:'relative', zIndex:2 }}>
          <div style={{ color:accent, fontSize:Math.round(fTitle*(textStyle?.titleSize||100)/100), fontFamily:textStyle?.fontFamily||"'Playfair Display',serif", fontWeight:textStyle?.titre?.bold!==false?700:400, lineHeight:1.2, marginBottom:Math.round(10*u) }}>{titre}</div>
          <div style={{ display:'flex', alignItems:'center', gap:Math.round(12*u) }}>
            <div style={{ width:Math.round(36*u), height:Math.round(2.5*u), background:accent }} />
            <div style={{ color:'rgba(255,255,255,0.72)', fontSize:Math.round(fSubT*(textStyle?.bodySize||100)/100), fontFamily:textStyle?.fontFamily||"'Raleway',sans-serif", fontWeight:textStyle?.sous_titre?.bold?600:300, letterSpacing:'0.02em' }}>{sous_titre}</div>
          </div>
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div style={{ flex:1, minHeight:0, overflow:'hidden', display:'flex', flexDirection:'column', padding:`${Math.round((isSquare?12:30)*u)}px ${padH}px ${Math.round((isSquare?8:20)*u)}px`, gap:Math.round((isSquare?8:20)*u) }}>

        {/* Services — items plus grands */}
        <div style={{ display:'flex', flexDirection:'column', gap:Math.round((isSquare?8:16)*u) }}>
          {services.filter(Boolean).map((s,i)=>(
            <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:Math.round(14*u) }}>
              <div style={{ width:iconSz, height:iconSz, borderRadius:'50%', background:headerBg, border:`${Math.round(2*u)}px solid ${accent}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:Math.round(2*u) }}>
                <span style={{ color:accent, fontSize:Math.round(13*u), fontWeight:700 }}>→</span>
              </div>
              <span style={{ color:'#1a1a2e', fontSize:Math.round(fSvc*(textStyle?.bodySize||100)/100), lineHeight:1.55, fontWeight:textStyle?.services?.bold?700:400, fontFamily:textStyle?.fontFamily||"'Raleway',sans-serif" }}>{s}</span>
            </div>
          ))}
        </div>

        {/* Stats — remplissage du vide sur les grands formats */}
        {showStats && (
          <div style={{ display:'flex', gap:Math.round(12*u) }}>
            {stats.filter(s=>s.value).map((s,i)=>(
              <div key={i} style={{ flex:1, textAlign:'center', padding:`${Math.round(14*u)}px ${Math.round(8*u)}px`, background:hexToRgba(headerBg,0.04), borderRadius:Math.round(10*u), border:`${Math.round(1*u)}px solid ${hexToRgba(headerBg,0.1)}` }}>
                <div style={{ fontSize:fStat, fontFamily:"'Playfair Display',serif", fontWeight:700, color:headerBg }}>{s.value}</div>
                <div style={{ fontSize:fStatLbl, color:'#888', marginTop:Math.round(3*u), fontWeight:400, letterSpacing:'0.04em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Séparateur doré */}
        <div style={{ display:'flex', alignItems:'center', gap:Math.round(12*u) }}>
          <div style={{ flex:1, height:Math.round(1.5*u), background:`linear-gradient(to right, ${accent}, transparent)` }} />
          <div style={{ width:Math.round(6*u), height:Math.round(6*u), borderRadius:'50%', background:accent }} />
          <div style={{ flex:1, height:Math.round(1.5*u), background:`linear-gradient(to left, ${accent}, transparent)` }} />
        </div>

        {/* QR bloc — plus grand */}
        <div style={{ display:'flex', alignItems:'center', gap:Math.round(24*u), padding:`${Math.round((isSquare?10:22)*u)}px ${Math.round((isSquare?12:24)*u)}px`, background:headerBg, borderRadius:Math.round(14*u), flexShrink:0 }}>
          <div style={{ padding:Math.round(8*u), background:'#fff', borderRadius:Math.round(10*u), flexShrink:0 }}>
            <QRCodeCanvas value={qrUrl} size={qrSize} fgColor={headerBg} bgColor="#ffffff" level="M" />
          </div>
          <div style={{ flex:1 }}>
            <div style={{ color:accent, fontSize:Math.round(fCta*(textStyle?.bodySize||100)/100), fontFamily:textStyle?.fontFamily||"'Playfair Display',serif", fontWeight:textStyle?.cta?.bold?700:500, marginBottom:Math.round(10*u), lineHeight:1.35 }}>{cta}</div>
            {phone   && <div style={{ color:'rgba(255,255,255,0.8)', fontSize:fContact, marginBottom:Math.round(5*u) }}>✆  {phone}</div>}
            {website && <div style={{ color:accent, fontSize:fContact, opacity:0.9 }}>⬡  {website}</div>}
          </div>
        </div>
      </div>

      {/* ══ FOOTER ══ */}
      <div style={{ height:footerH, background:hexToRgba(accent,0.07), borderTop:`${Math.round(1.5*u)}px solid ${hexToRgba(accent,0.25)}`, display:'flex', alignItems:'center', justifyContent:'space-between', padding:`0 ${padH}px`, flexShrink:0 }}>
        <div style={{ fontSize:fFooter, color:'#666', letterSpacing:'0.06em', textTransform:'uppercase' }}>{agencyName} — Service d'audit immobilier</div>
        <div style={{ fontSize:fFooter, color:headerBg, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase' }}>100 % Gratuit</div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   A4 PAYSAGE
════════════════════════════════════════════════════ */
function LandscapePoster({ branding, texts, qrUrl, w, h, colors, textStyle = {} }) {
  const { logoUrl, agencyName, phone, website } = branding;
  const { titre, sous_titre, services, cta } = texts;
  const { headerBg, accent, headerImg, headerOverlay } = colors;
  const u = w / 1123;

  const padV = Math.round(36*u), padH = Math.round(44*u);
  const logoH = Math.round(50*u);
  const scaledLogoH = Math.round(logoH * (textStyle?.agencySize||100)/100);
  const fAgency = Math.round(22*u);
  const fTitle  = Math.round(32*u);
  const fSubT   = Math.round(14*u);
  const fSvc    = Math.round(14*u);
  const fContact= Math.round(12*u);
  const fCta    = Math.round(15*u);
  const qrSize  = Math.round(210 * u * (textStyle.qrScale||100)/100);

  return (
    <div style={{ width:w, height:h, background:'#fff', display:'flex', fontFamily:"'Raleway','Segoe UI',sans-serif", overflow:'hidden' }}>

      {/* Colonne gauche — fond */}
      <div style={{ width:Math.round(w*0.52), position:'relative', overflow:'hidden', display:'flex', flexDirection:'column', padding:`${padV}px ${padH}px`, justifyContent:'space-between' }}>
        {headerImg
          ? <><img src={headerImg} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }} /><div style={{ position:'absolute', inset:0, background:hexToRgba(headerBg, headerOverlay??0.78) }} /></>
          : <div style={{ position:'absolute', inset:0, background:headerBg }} />
        }
        {/* Logo */}
        <div style={{ display:'flex', alignItems:'center', gap:Math.round(12*u), position:'relative', zIndex:2 }}>
          {logoUrl
            ? <img src={logoUrl} alt="logo" style={{ height:scaledLogoH, maxWidth:Math.round(120*u), objectFit:'contain' }} />
            : <div style={{ height:scaledLogoH, width:scaledLogoH, borderRadius:Math.round(8*u), background:hexToRgba(accent,0.2), border:`2px solid ${hexToRgba(accent,0.5)}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ color:accent, fontWeight:700, fontSize:Math.round(20*u) }}>{(agencyName||'A').charAt(0)}</span>
              </div>
          }
          <div>
            <div style={{ color:'#fff', fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:Math.round(fAgency*(textStyle?.agencySize||100)/100) }}>{agencyName}</div>
            <div style={{ color:hexToRgba(accent,0.8), fontSize:Math.round(10*u), fontWeight:300, letterSpacing:'0.1em', textTransform:'uppercase', marginTop:Math.round(2*u) }}>Immobilier de prestige</div>
          </div>
        </div>
        {/* Titre + services */}
        <div style={{ position:'relative', zIndex:2 }}>
          <div style={{ color:accent, fontFamily:textStyle?.fontFamily||"'Playfair Display',serif", fontWeight:textStyle?.titre?.bold!==false?700:400, fontSize:Math.round(fTitle*(textStyle?.titleSize||100)/100), lineHeight:1.2, marginBottom:Math.round(10*u) }}>{titre}</div>
          <div style={{ display:'flex', alignItems:'center', gap:Math.round(10*u), marginBottom:Math.round(18*u) }}>
            <div style={{ width:Math.round(26*u), height:Math.round(2.5*u), background:accent }} />
            <div style={{ color:'rgba(255,255,255,0.65)', fontSize:Math.round(fSubT*(textStyle?.bodySize||100)/100), fontWeight:textStyle?.sous_titre?.bold?600:300, fontFamily:textStyle?.fontFamily||"'Raleway',sans-serif" }}>{sous_titre}</div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:Math.round(11*u) }}>
            {services.filter(Boolean).map((s,i)=>(
              <div key={i} style={{ display:'flex', alignItems:'center', gap:Math.round(10*u) }}>
                <div style={{ width:Math.round(7*u), height:Math.round(7*u), borderRadius:'50%', background:accent, flexShrink:0 }} />
                <span style={{ color:'rgba(255,255,255,0.88)', fontSize:Math.round(fSvc*(textStyle?.bodySize||100)/100), fontWeight:textStyle?.services?.bold?700:400, fontFamily:textStyle?.fontFamily||"'Raleway',sans-serif" }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Contact */}
        <div style={{ display:'flex', flexDirection:'column', gap:Math.round(5*u), position:'relative', zIndex:2 }}>
          {phone   && <div style={{ color:'rgba(255,255,255,0.75)', fontSize:fContact }}>✆  {phone}</div>}
          {website && <div style={{ color:accent, fontSize:fContact }}>⬡  {website}</div>}
        </div>
      </div>

      {/* Colonne droite — QR */}
      <div style={{ flex:1, background:'#fff', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:Math.round(36*u), gap:Math.round(20*u) }}>
        <div style={{ background:headerBg, color:accent, fontSize:Math.round(11*u), fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', padding:`${Math.round(7*u)}px ${Math.round(18*u)}px`, borderRadius:Math.round(22*u) }}>Service offert</div>
        <div style={{ padding:Math.round(14*u), background:'#fff', border:`${Math.round(2.5*u)}px solid ${hexToRgba(accent,0.45)}`, borderRadius:Math.round(12*u) }}>
          <QRCodeCanvas value={qrUrl} size={qrSize} fgColor={headerBg} bgColor="#ffffff" level="M" />
        </div>
        <div style={{ textAlign:'center', maxWidth:Math.round(210*u) }}>
          <div style={{ fontSize:fCta, fontFamily:"'Playfair Display',serif", fontWeight:600, color:headerBg, marginBottom:Math.round(8*u), lineHeight:1.35 }}>{cta}</div>
          <div style={{ width:Math.round(32*u), height:Math.round(2.5*u), background:accent, margin:'0 auto' }} />
        </div>
        {phone   && <div style={{ fontSize:Math.round(12*u), color:'#555' }}>✆  {phone}</div>}
        {website && <div style={{ fontSize:Math.round(12*u), color:headerBg, fontWeight:500 }}>⬡  {website}</div>}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   CARTE DE VISITE
════════════════════════════════════════════════════ */
function CardPoster({ branding, texts, qrUrl, w, h, colors, textStyle = {} }) {
  const { logoUrl, agencyName, phone, website } = branding;
  const { titre } = texts;
  const { headerBg, accent, headerImg, headerOverlay } = colors;
  const u = w / 321;
  const qrSize = Math.round(76*u * (textStyle.qrScale||100)/100);

  return (
    <div style={{ width:w, height:h, display:'flex', fontFamily:"'Raleway','Segoe UI',sans-serif", overflow:'hidden', position:'relative' }}>
      {headerImg
        ? <><img src={headerImg} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }} /><div style={{ position:'absolute', inset:0, background:hexToRgba(headerBg, headerOverlay??0.82) }} /></>
        : <div style={{ position:'absolute', inset:0, background:headerBg }} />
      }
      <div style={{ width:Math.round(7*u), background:accent, flexShrink:0, position:'relative', zIndex:2 }} />
      <div style={{ flex:1, display:'flex', alignItems:'center', padding:`${Math.round(13*u)}px ${Math.round(17*u)}px`, gap:Math.round(15*u), position:'relative', zIndex:2 }}>
        <div style={{ flex:1, display:'flex', flexDirection:'column', gap:Math.round(5*u) }}>
          {logoUrl && <img src={logoUrl} alt="logo" style={{ height:Math.round(26*u * (textStyle?.agencySize||100)/100), maxWidth:Math.round(65*u), objectFit:'contain', marginBottom:Math.round(3*u) }} />}
          <div style={{ color:accent, fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:Math.round(15*u * (textStyle?.agencySize||100)/100) }}>{agencyName}</div>
          <div style={{ color:'rgba(255,255,255,0.5)', fontSize:Math.round(8*u), letterSpacing:'0.08em', textTransform:'uppercase' }}>Immobilier de prestige</div>
          <div style={{ height:Math.round(1*u), width:Math.round(22*u), background:hexToRgba(accent,0.5), margin:`${Math.round(2*u)}px 0` }} />
          <div style={{ color:'rgba(255,255,255,0.78)', fontSize:Math.round(8.5*u * (textStyle?.bodySize||100)/100), lineHeight:1.4 }}>{titre}</div>
          {phone   && <div style={{ color:'rgba(255,255,255,0.65)', fontSize:Math.round(8*u), marginTop:Math.round(2*u) }}>✆ {phone}</div>}
          {website && <div style={{ color:accent, fontSize:Math.round(8*u), opacity:0.9 }}>⬡ {website}</div>}
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:Math.round(5*u) }}>
          <div style={{ padding:Math.round(5*u), background:'#fff', borderRadius:Math.round(4*u) }}>
            <QRCodeCanvas value={qrUrl} size={qrSize} fgColor={headerBg} bgColor="#ffffff" level="M" />
          </div>
          <div style={{ color:'rgba(255,255,255,0.45)', fontSize:Math.round(7*u) }}>Scan</div>
        </div>
      </div>
    </div>
  );
}

/* ── Dispatcher ── */
function PosterContent({ format, branding, texts, qrUrl, colors, textStyle }) {
  const { id, w, h } = format;
  if (id === 'carte_visite') return <CardPoster branding={branding} texts={texts} qrUrl={qrUrl} w={w} h={h} colors={colors} textStyle={textStyle} />;
  if (id === 'a4_paysage')   return <LandscapePoster branding={branding} texts={texts} qrUrl={qrUrl} w={w} h={h} colors={colors} textStyle={textStyle} />;
  return <PortraitPoster branding={branding} texts={texts} qrUrl={qrUrl} w={w} h={h} colors={colors} textStyle={textStyle} isSquare={id==='carre'} />;
}

/* ════════════════════════════════════════════════════
   MODAL IMPRESSION
════════════════════════════════════════════════════ */
function PrintModal({ format, branding, texts, qrUrl, colors, textStyle, onClose }) {
  const ref      = useRef(null);   // div scalée (aperçu)
  const fullRef  = useRef(null);   // div full-size cachée (pour téléchargement)
  const [downloading, setDownloading] = useState(false);

  /* ── Imprimer (via html2canvas → image → popup) ── */
  const handlePrint = async () => {
    if (!fullRef.current) return;
    toast.info('Préparation de l\'impression…');
    try {
      /* 1. Convertir le poster en PNG haute résolution */
      const canvas = await html2canvas(fullRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        backgroundColor: '#ffffff',
        width: format.w,
        height: format.h,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png', 1.0);

      const { w, h } = format;
      const isLandscape = w > h;
      const orientation = isLandscape ? 'landscape' : 'portrait';
      const isCard = format.id === 'carte_visite';
      const mmW = (w / 3.7795).toFixed(1);
      const mmH = (h / 3.7795).toFixed(1);

      /* Popup aux proportions A4 pour aider Edge à choisir la bonne orientation */
      const popW = isLandscape ? 960 : 677;
      const popH = isLandscape ? 677 : 960;

      /* 2. Ouvrir une popup avec seulement l'image */
      const win = window.open('', '_blank', `width=${popW},height=${popH}`);
      if (!win) { toast.error('Popup bloquée — autorisez les popups ou utilisez Télécharger PNG.'); return; }

      /* Carte de visite → taille physique réelle centrée (85×55mm)
         Autres formats  → 100vw/100vh = dimensions du PAPIER en mode impression,
                           indépendant du dialogue de l'imprimante */
      const bodyStyle = isCard ? 'display:flex;align-items:center;justify-content:center;' : '';
      const imgStyle  = isCard
        ? `display:block;width:${mmW}mm;height:${mmH}mm;`
        : 'display:block;width:100vw;height:100vh;object-fit:contain;';

      win.document.write(`<!DOCTYPE html><html><head>
<meta charset="utf-8"/><title>${format.label}</title>
<style>
@page{size:${orientation};margin:0}
*{margin:0;padding:0;box-sizing:border-box}
html,body{margin:0;padding:0;width:100%;height:100%;background:#fff;${bodyStyle}}
img{${imgStyle}background:#fff}
</style>
</head><body><img src="${imgData}"/></body></html>`);
      win.document.close();

      /* 3. Lancer l'impression puis fermer automatiquement */
      win.onload = () => {
        setTimeout(() => {
          win.focus();
          win.print();
        }, 400);
      };
      win.onafterprint = () => win.close();

    } catch(e) {
      console.error(e);
      toast.error('Erreur — utilisez Télécharger PNG puis imprimez l\'image.');
    }
  };

  /* ── Télécharger PNG ── */
  const handleDownload = async () => {
    if (!fullRef.current) return;
    setDownloading(true);
    toast.info('Génération de l\'image…');
    try {
      const canvas = await html2canvas(fullRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 2,             // haute résolution
        backgroundColor: '#ffffff',
        width: format.w,
        height: format.h,
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `affiche-${format.id}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      toast.success('Affiche téléchargée !');
    } catch(e) {
      toast.error('Erreur de génération PNG — essayez Imprimer / PDF');
    } finally {
      setDownloading(false);
    }
  };

  const maxW  = Math.min(window.innerWidth*0.9, 960);
  const maxH  = window.innerHeight * 0.76;
  const scale = Math.min(maxW/format.w, maxH/format.h, 1);

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(5,10,25,0.85)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:14, overflow:'hidden', maxWidth:960, width:'100%', display:'flex', flexDirection:'column', maxHeight:'95vh' }}>

        {/* Header */}
        <div style={{ padding:'14px 20px', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontFamily:"'Raleway',sans-serif", fontWeight:700, fontSize:15 }}>{format.icon} {format.label}</div>
            <div style={{ fontSize:12, color:'#888' }}>{format.sub}</div>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
            <button
              onClick={handleDownload}
              disabled={downloading}
              style={{ display:'flex', alignItems:'center', gap:6, background:downloading?'#6b7280':'#1a7a4a', color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:700, cursor: downloading?'not-allowed':'pointer', opacity: downloading?0.7:1 }}
              data-testid="modal-download-btn"
            >
              <Download size={15} /> {downloading ? 'Génération…' : 'Télécharger PNG'}
            </button>
            <button
              onClick={handlePrint}
              style={{ display:'flex', alignItems:'center', gap:6, background:'#0B1426', color:'#C9A96E', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:700, cursor:'pointer' }}
              data-testid="modal-print-btn"
            >
              <Printer size={15} /> Imprimer / PDF
            </button>
            <button onClick={onClose} style={{ background:'#f3f4f6', border:'none', borderRadius:8, padding:8, cursor:'pointer', display:'flex' }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Aperçu (scalé) */}
        <div style={{ overflowY:'auto', overflowX:'auto', padding:24, background:'#f0f2f5', display:'flex', justifyContent:'center' }}>
          <div ref={ref} style={{ width:format.w*scale, height:format.h*scale, overflow:'hidden', flexShrink:0, boxShadow:'0 8px 40px rgba(0,0,0,0.2)', position:'relative' }}>
            <div data-poster style={{ transform:`scale(${scale})`, transformOrigin:'top left', width:format.w, height:format.h }}>
              <PosterContent format={format} branding={branding} texts={texts} qrUrl={qrUrl} colors={colors} textStyle={textStyle} />
            </div>
          </div>
        </div>

        {/* Div full-size cachée pour html2canvas — masquée à l'impression */}
        <div data-no-print style={{ position:'fixed', left:'-9999px', top:0, zIndex:-1, overflow:'hidden', pointerEvents:'none' }}>
          <div ref={fullRef} style={{ width:format.w, height:format.h }}>
            <PosterContent format={format} branding={branding} texts={texts} qrUrl={qrUrl} colors={colors} textStyle={textStyle} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   COLOR PICKER
════════════════════════════════════════════════════ */
function ColorPicker({ label, value, onChange, presets }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">{label}</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {presets.map(p => (
          <button key={p.value} onClick={()=>onChange(p.value)} title={p.name}
            className={`w-8 h-8 rounded-lg transition-all ${value===p.value?'ring-2 ring-offset-1 ring-blue-500 scale-110':'hover:scale-105'}`}
            style={{ background:p.value, border:'2px solid rgba(0,0,0,0.12)' }} />
        ))}
        <input type="color" value={value} onChange={e=>onChange(e.target.value)} className="w-8 h-8 rounded-lg cursor-pointer border-2 border-gray-200" title="Couleur personnalisée" />
      </div>
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded" style={{ background:value, border:'1px solid rgba(0,0,0,0.15)' }} />
        <span className="text-xs text-gray-500 font-mono">{value}</span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
════════════════════════════════════════════════════ */
export default function KitPubSection({ branding, onBack }) {
  const fileInputRef = useRef(null);

  /* ── Initialisation depuis localStorage ── */
  const getFormatKey = (fmtId) => `${LS_KEY}_${branding.subdomain || 'default'}_${fmtId}`;

  const getSavedForFormat = (fmtId) => {
    try {
      const raw = localStorage.getItem(getFormatKey(fmtId));
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };

  // Compatibilité descendante : essaie d'abord la clé par format, puis l'ancienne clé globale
  const saved = getSavedForFormat('a4_portrait') ||
    (()=>{ try { const r=localStorage.getItem(`${LS_KEY}_${branding.subdomain||'default'}`); return r?JSON.parse(r):null; } catch{return null;} })();

  const [texts, setTexts] = useState(saved?.texts || {
    titre:'', sous_titre:'', services:['','','',''], cta:'',
    stats:[ {value:'100%',label:'Gratuit'}, {value:'48h',label:'Délai de réponse'}, {value:'500+',label:'Clients'} ],
  });
  const [kitColors, setKitColors] = useState(saved?.kitColors || {
    headerBg: '#0B1426',
    accent:   branding.accentColor || '#C9A96E',
    headerImg:'',
    headerOverlay: 0.72,
  });
  const [textStyle, setTextStyle] = useState(saved?.textStyle || {
    fontFamily: "'Playfair Display',serif",
    qrScale:    100,
    titleSize:  100,
    bodySize:   100,
    agencySize: 100,
    titre:      { bold: true },
    sous_titre: { bold: false },
    services:   { bold: false },
    cta:        { bold: false },
  });
  const [focusedField, setFocusedField] = useState('titre');
  const [previewFormatId, setPreviewFormatId] = useState('a4_portrait');
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [savedFormatIds, setSavedFormatIds] = useState(() =>
    FORMATS.filter(f => { try { return !!localStorage.getItem(getFormatKey(f.id)); } catch { return false; } }).map(f => f.id)
  );
  const prevFormatIdRef = useRef('a4_portrait');

  const availableImages = [
    branding.heroImageUrl    && { label:'Photo héro',    url:branding.heroImageUrl },
    branding.aboutImageUrl   && { label:'Photo à propos',url:branding.aboutImageUrl },
    branding.galleryImageUrl && { label:'Photo galerie', url:branding.galleryImageUrl },
  ].filter(Boolean);

  useEffect(()=>{ ensureFont(); },[]);

  /* Chargement des réglages au changement de format */
  useEffect(() => {
    if (prevFormatIdRef.current === previewFormatId) { prevFormatIdRef.current = previewFormatId; return; }
    prevFormatIdRef.current = previewFormatId;
    const fmtSaved = getSavedForFormat(previewFormatId);
    if (!fmtSaved) return;
    if (fmtSaved.texts)     setTexts(fmtSaved.texts);
    if (fmtSaved.kitColors) setKitColors(fmtSaved.kitColors);
    if (fmtSaved.textStyle) setTextStyle(prev => ({
      fontFamily:"'Playfair Display',serif", qrScale:100, titleSize:100, bodySize:100, agencySize:100,
      titre:{bold:true}, sous_titre:{bold:false}, services:{bold:false}, cta:{bold:false},
      ...fmtSaved.textStyle
    }));
  }, [previewFormatId]);

  const subdomain = branding.subdomain || '';
  const qrUrl = subdomain ? `${window.location.origin}/p/${subdomain}` : window.location.origin;

  const displayTexts = {
    titre:      texts.titre      || EXEMPLE.titre,
    sous_titre: texts.sous_titre || EXEMPLE.sous_titre,
    services:   texts.services.map((s,i)=>s||EXEMPLE.services[i]||''),
    cta:        texts.cta        || EXEMPLE.cta,
    stats:      texts.stats,
  };

  /* ── Sauvegarder ── */
  const handleSave = () => {
    try {
      localStorage.setItem(getFormatKey(previewFormatId), JSON.stringify({ texts, kitColors, textStyle }));
      if (!savedFormatIds.includes(previewFormatId)) setSavedFormatIds(ids => [...ids, previewFormatId]);
      const fmtLabel = (FORMATS.find(f=>f.id===previewFormatId)||FORMATS[0]).label;
      toast.success(`Enregistré pour ${fmtLabel} !`);
    } catch { toast.error('Erreur lors de l\'enregistrement'); }
  };

  /* ── Upload image depuis fichier ── */
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Veuillez sélectionner une image.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image trop grande (max 5 Mo)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setKitColors(c => ({ ...c, headerImg: ev.target.result }));
      toast.success('Image ajoutée !');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const fillExample = () => { setTexts(t=>({ ...t, ...EXEMPLE, services:[...EXEMPLE.services] })); toast.success('Exemple chargé !'); };

  const handleDownloadQR = () => {
    const c = document.querySelector('[data-qr-main] canvas');
    if (!c) { toast.error('QR introuvable'); return; }
    const a = document.createElement('a'); a.download=`qrcode-${subdomain||'partenaire'}.png`; a.href=c.toDataURL('image/png'); a.click();
    toast.success('QR Code téléchargé !');
  };

  return (
    <div className="space-y-5">
      {/* Nav + bouton Enregistrer */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <QrCode className="w-5 h-5 text-blue-600" />
          <div>
            <h2 className="font-semibold text-lg text-gray-900">Kit Communication</h2>
            <p className="text-xs text-gray-500">QR Code + Affiches premium prêtes à imprimer</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-4 py-2 bg-[#0B1426] hover:bg-[#162040] text-[#C9A96E] rounded-lg text-sm font-semibold transition-colors"
          data-testid="kit-pub-save-btn"
        >
          <Save className="w-4 h-4" /> Enregistrer
        </button>
      </div>

      {/* QR Code */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><QrCode className="w-4 h-4 text-blue-600" /> Votre QR Code</h3>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div data-qr-main className="p-4 bg-white border-2 border-gray-100 rounded-xl shadow-sm">
              <QRCodeCanvas value={qrUrl} size={120} fgColor={kitColors.headerBg} bgColor="#ffffff" level="H" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 font-medium mb-1">Lien de votre page client :</p>
              <p className="text-sm text-blue-600 font-mono bg-blue-50 rounded-lg px-3 py-2 mb-3 break-all">{qrUrl}</p>
              {!subdomain && <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-3">Configurez un sous-domaine dans "Personnalisation" pour un QR code personnalisé.</p>}
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={handleDownloadQR} data-testid="download-qr-btn" className="flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Télécharger QR</Button>
                <a href={qrUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5" /> Voir la page</Button></a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Textes */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Type className="w-4 h-4 text-purple-500" /> Textes des affiches</h3>
            <button onClick={fillExample} className="text-xs px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 transition-colors flex items-center gap-1.5" data-testid="kit-pub-exemple-btn">
              <Sparkles className="w-3.5 h-3.5" /> Exemple agence immo
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Titre principal</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none" value={texts.titre} onChange={e=>setTexts(t=>({...t,titre:e.target.value}))} onFocus={()=>setFocusedField('titre')} placeholder={EXEMPLE.titre} maxLength={60} data-testid="kit-pub-titre" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Sous-titre</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none" value={texts.sous_titre} onChange={e=>setTexts(t=>({...t,sous_titre:e.target.value}))} onFocus={()=>setFocusedField('sous_titre')} placeholder={EXEMPLE.sous_titre} maxLength={80} data-testid="kit-pub-sous-titre" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">4 services mis en avant</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {texts.services.map((s,i)=>(
                  <input key={i} className="border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                    value={s} onChange={e=>{const a=[...texts.services];a[i]=e.target.value;setTexts(t=>({...t,services:a}));}}
                    onFocus={()=>setFocusedField('services')}
                    placeholder={EXEMPLE.services[i]} maxLength={60} data-testid={`kit-pub-service-${i}`} />
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Texte sous le QR code</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none" value={texts.cta} onChange={e=>setTexts(t=>({...t,cta:e.target.value}))} onFocus={()=>setFocusedField('cta')} placeholder={EXEMPLE.cta} maxLength={80} data-testid="kit-pub-cta" />
            </div>
            {/* Stats */}
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Chiffres clés (remplissent le vide)</label>
              <div className="grid grid-cols-3 gap-2">
                {texts.stats.map((s,i)=>(
                  <div key={i} className="space-y-1">
                    <input className="w-full border rounded-lg px-2 py-1.5 text-sm text-center font-bold focus:ring-1 focus:ring-blue-400 focus:outline-none" value={s.value} onChange={e=>{const a=[...texts.stats];a[i]={...a[i],value:e.target.value};setTexts(t=>({...t,stats:a}));}} placeholder={EXEMPLE.stats[i].value} maxLength={8} data-testid={`kit-stat-value-${i}`} />
                    <input className="w-full border rounded-lg px-2 py-1.5 text-xs text-center focus:ring-1 focus:ring-blue-400 focus:outline-none" value={s.label} onChange={e=>{const a=[...texts.stats];a[i]={...a[i],label:e.target.value};setTexts(t=>({...t,stats:a}));}} placeholder={EXEMPLE.stats[i].label} maxLength={20} data-testid={`kit-stat-label-${i}`} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">Laissez vide pour masquer un chiffre</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Style des textes */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Type className="w-4 h-4 text-indigo-500" /> Style des textes & QR Code</h3>
          <div className="flex flex-col lg:flex-row gap-6 items-start">

            {/* ── Contrôles ── */}
            <div className="flex-1 space-y-4 min-w-0">
              <div className="flex items-center gap-2 p-2.5 bg-indigo-50 rounded-lg border border-indigo-100">
                <div className="w-2 h-2 rounded-full bg-indigo-500" style={{boxShadow:'0 0 0 3px rgba(99,102,241,0.2)'}} />
                <span className="text-sm font-semibold text-indigo-700">Style actif : {FIELD_LABELS[focusedField]}</span>
                <span className="text-xs text-indigo-400 ml-auto hidden sm:block">Cliquez dans un champ</span>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">Police (globale)</label>
                <select value={textStyle.fontFamily} onChange={e => setTextStyle(s => ({ ...s, fontFamily: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none bg-white" data-testid="kit-font-select">
                  {FONT_OPTIONS.map(f => (<option key={f.value} value={f.value}>{f.label}</option>))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Graisse — {FIELD_LABELS[focusedField]}</label>
                <div className="flex gap-2">
                  <button onClick={() => setTextStyle(s => ({ ...s, [focusedField]: { ...(s[focusedField]||{}), bold: true } }))} className={`flex-1 py-2 rounded-lg border-2 text-sm font-bold transition-all ${textStyle[focusedField]?.bold ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`} data-testid="kit-bold-on">Gras</button>
                  <button onClick={() => setTextStyle(s => ({ ...s, [focusedField]: { ...(s[focusedField]||{}), bold: false } }))} className={`flex-1 py-2 rounded-lg border-2 text-sm transition-all ${!textStyle[focusedField]?.bold ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`} data-testid="kit-bold-off">Normal</button>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Taille du titre</label>
                  <span className="text-xs font-bold text-blue-600">{textStyle.titleSize || 100}%</span>
                </div>
                <input type="range" min="70" max="140" step="5" value={textStyle.titleSize || 100} onChange={e => setTextStyle(s => ({ ...s, titleSize: parseInt(e.target.value) }))} className="w-full accent-blue-600" data-testid="kit-title-size" />
                <div className="flex justify-between text-xs text-gray-400"><span>Petit</span><span>Grand</span></div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Taille des textes</label>
                  <span className="text-xs font-bold text-purple-600">{textStyle.bodySize || 100}%</span>
                </div>
                <input type="range" min="70" max="130" step="5" value={textStyle.bodySize || 100} onChange={e => setTextStyle(s => ({ ...s, bodySize: parseInt(e.target.value) }))} className="w-full accent-purple-600" data-testid="kit-body-size" />
                <div className="flex justify-between text-xs text-gray-400"><span>Petit</span><span>Grand</span></div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Taille du QR Code</label>
                  <span className="text-xs font-bold text-green-600">{textStyle.qrScale}%</span>
                </div>
                <input type="range" min="50" max="180" step="10" value={textStyle.qrScale} onChange={e => setTextStyle(s => ({ ...s, qrScale: parseInt(e.target.value) }))} className="w-full accent-green-600" data-testid="kit-qr-scale" />
                <div className="flex justify-between text-xs text-gray-400"><span>Petit</span><span>Grand</span></div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Taille Logo & Nom agence</label>
                  <span className="text-xs font-bold text-amber-600">{textStyle.agencySize || 100}%</span>
                </div>
                <input type="range" min="60" max="150" step="5" value={textStyle.agencySize || 100} onChange={e => setTextStyle(s => ({ ...s, agencySize: parseInt(e.target.value) }))} className="w-full accent-amber-600" data-testid="kit-agency-size" />
                <div className="flex justify-between text-xs text-gray-400"><span>Petit</span><span>Grand</span></div>
              </div>
            </div>

            {/* ── Aperçu live ── */}
            <div className="flex flex-col items-center gap-2" style={{ flexShrink:0 }}>
              <div className="flex items-center justify-between w-full">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Aperçu en direct</label>
                <button onClick={handleSave} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm" data-testid="kit-save-format">
                  <Save className="w-3 h-3" /> Enregistrer
                </button>
              </div>
              <div className="flex flex-wrap gap-1 self-start">
                {FORMATS.map(f=>(
                  <button key={f.id} onClick={()=>setPreviewFormatId(f.id)}
                    className={`relative px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${previewFormatId===f.id?'bg-indigo-600 text-white shadow-sm':'bg-gray-100 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'}`}
                  >
                    {FORMAT_SHORT[f.id]}
                    {savedFormatIds.includes(f.id) && <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full border border-white" />}
                  </button>
                ))}
              </div>
              {(()=>{
                const fmt=FORMATS.find(f=>f.id===previewFormatId)||FORMATS[0];
                const sc=Math.min(280/fmt.w, 420/fmt.h);
                const cW=Math.round(fmt.w*sc), cH=Math.round(fmt.h*sc);
                return (
                  <div style={{width:cW, height:cH, overflow:'hidden', borderRadius:10, boxShadow:'0 6px 28px rgba(0,0,0,0.2)', position:'relative', background:'#fff', transition:'width 0.3s,height 0.3s'}}>
                    <div style={{position:'absolute', top:0, left:0, width:fmt.w, height:fmt.h, transformOrigin:'top left', transform:`scale(${sc.toFixed(4)})`, pointerEvents:'none'}}>
                      <PosterContent format={fmt} branding={branding} texts={displayTexts} qrUrl={qrUrl} colors={kitColors} textStyle={textStyle} />
                    </div>
                  </div>
                );
              })()}
              <p className="text-xs text-gray-400 text-center">{(FORMATS.find(f=>f.id===previewFormatId)||FORMATS[0]).label} — mis à jour en direct</p>
            </div>

          </div>
        </CardContent>
      </Card>

      {/* Couleurs */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Palette className="w-4 h-4 text-orange-500" /> Couleurs des affiches</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            <ColorPicker label="Fond (zone sombre)" value={kitColors.headerBg} onChange={v=>setKitColors(c=>({...c,headerBg:v}))} presets={COLOR_PRESETS_BG} />
            <ColorPicker label="Accent / couleur dorée" value={kitColors.accent} onChange={v=>setKitColors(c=>({...c,accent:v}))} presets={COLOR_PRESETS_ACCENT} />
          </div>
          <div className="mt-4 p-3 rounded-lg border border-dashed border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500 mb-2">Aperçu :</p>
            <div className="h-10 rounded-lg flex items-center justify-between px-4" style={{ background:kitColors.headerBg }}>
              <span style={{ color:kitColors.accent, fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:14 }}>Nom de votre agence</span>
              <div style={{ width:28, height:4, borderRadius:2, background:kitColors.accent }} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Image de fond */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2"><Image className="w-4 h-4 text-green-500" /> Image de fond du header</h3>
          <p className="text-xs text-gray-500 mb-4">Apparaît derrière les textes dans la zone sombre — un overlay coloré maintient la lisibilité</p>
          {availableImages.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Vos photos de vitrine</p>
              <div className="grid grid-cols-3 gap-2">
                {availableImages.map((img,i)=>(
                  <button key={i} onClick={()=>setKitColors(c=>({...c,headerImg:img.url}))}
                    className={`relative rounded-lg overflow-hidden border-2 transition-all aspect-video ${kitColors.headerImg===img.url?'border-blue-500 ring-2 ring-blue-300':'border-gray-200 hover:border-blue-300'}`}
                    data-testid={`kit-img-preset-${i}`}>
                    <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                    {kitColors.headerImg===img.url && <div style={{ position:'absolute', top:4, right:4, background:'#3b82f6', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ color:'#fff', fontSize:10, fontWeight:700 }}>✓</span></div>}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Upload fichier depuis ordinateur */}
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Importer depuis votre ordinateur</p>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" data-testid="kit-file-input" />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-lg text-sm text-gray-600 hover:text-blue-600 transition-colors w-full justify-center"
              data-testid="kit-upload-btn"
            >
              <Upload className="w-4 h-4" /> Choisir une image (JPG, PNG — max 5 Mo)
            </button>
            {kitColors.headerImg?.startsWith('data:') && (
              <div className="mt-2 flex items-center gap-2">
                <img src={kitColors.headerImg} alt="aperçu" className="w-12 h-8 object-cover rounded border" />
                <span className="text-xs text-green-600 font-medium">Image importée</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block">Ou coller une URL d'image</label>
            <input className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none" value={kitColors.headerImg?.startsWith('data:') ? '' : kitColors.headerImg} onChange={e=>setKitColors(c=>({...c,headerImg:e.target.value}))} placeholder="https://... ou laissez vide pour fond uni" data-testid="kit-img-url" />
          </div>
          {kitColors.headerImg && (
            <div className="mt-4">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Opacité overlay — {Math.round(kitColors.headerOverlay*100)} %</label>
              <input type="range" min="30" max="95" step="5" value={Math.round(kitColors.headerOverlay*100)} onChange={e=>setKitColors(c=>({...c,headerOverlay:parseInt(e.target.value)/100}))} className="w-full accent-blue-600" />
              <div className="flex justify-between text-xs text-gray-400 mt-1"><span>Image visible</span><span>Image masquée</span></div>
            </div>
          )}
          {kitColors.headerImg && <button onClick={()=>setKitColors(c=>({...c,headerImg:''}))} className="mt-3 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"><X className="w-3 h-3" /> Supprimer l'image</button>}
        </CardContent>
      </Card>

      {/* Grille formats */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Printer className="w-4 h-4 text-gray-600" /> Formats à imprimer</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {FORMATS.map(fmt=>(
            <button key={fmt.id} onClick={()=>setSelectedFormat(fmt)} data-testid={`kit-pub-format-${fmt.id}`}
              className="group relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-amber-400 bg-white hover:bg-amber-50/30 transition-all">
              <div className="text-2xl">{fmt.icon}</div>
              <div>
                <div className="text-sm font-semibold text-gray-800 text-center">{fmt.label}</div>
                <div className="text-xs text-gray-500 text-center">{fmt.sub}</div>
              </div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-[#0B1426] text-[#C9A96E] text-xs px-2 py-0.5 rounded-full flex items-center gap-1"><Eye size={10} /> Aperçu</div>
              </div>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3 text-center">Logo, couleurs et contacts proviennent automatiquement de votre Personnalisation</p>
      </div>

      {selectedFormat && (
        <PrintModal format={selectedFormat} branding={branding} texts={displayTexts} qrUrl={qrUrl} colors={kitColors} textStyle={textStyle} onClose={()=>setSelectedFormat(null)} />
      )}
    </div>
  );
}
